import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useGame } from "../context/GameContext";
import { SUBJECTS, DAILY_CHALLENGES } from "../lib/content";
import { ASSETS } from "../lib/assets";
import TopBar from "../components/TopBar";
import Mascot from "../components/Mascot";
import { speak } from "../lib/speech";
import { sound } from "../lib/sounds";
import { Lock, PawPrint, Settings, ShieldCheck, Trophy, Target } from "lucide-react";

const NavBtn = ({ icon: Icon, label, color, onClick, testid }) => (
  <motion.button
    data-testid={testid}
    whileTap={{ scale: 0.92 }} whileHover={{ y: -3 }}
    onClick={onClick}
    className="flex flex-1 flex-col items-center gap-1 rounded-2xl bg-white/90 py-2 shadow-md"
  >
    <Icon className="h-6 w-6" style={{ color }} />
    <span className="font-display text-xs font-bold text-slate-600">{label}</span>
  </motion.button>
);

export default function Home() {
  const { profile } = useGame();
  const navigate = useNavigate();

  const daily = useMemo(() => DAILY_CHALLENGES[(profile?.completed_lessons?.length || 0) % DAILY_CHALLENGES.length], [profile]);

  useEffect(() => {
    if (profile) { const t = setTimeout(() => speak(`Hoi ${profile.name}! Kies een eiland om te spelen.`), 500); return () => clearTimeout(t); }
  }, [profile]);

  if (!profile) return null;

  const openSubject = (s) => {
    if (!s.unlocked) { sound.wrong(); speak("Dit eiland is nog op slot. Speel meer om het te openen!"); return; }
    sound.tap();
    navigate(`/vak/${s.id}`);
  };

  const visibleSubjects = SUBJECTS.filter((s) => (profile.enabled_subjects || {})[s.id] !== false);

  return (
    <div className="min-h-screen relative"
         style={{ backgroundImage: `url(${ASSETS.worldBg})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>
      <div className="absolute inset-0 bg-white/25 backdrop-blur-[1px]" />
      <div className="relative z-10">
        <TopBar showHome={false} />

        <div className="mx-auto max-w-3xl px-4 pb-28 pt-2">
          {/* Mascot greeting */}
          <div className="flex items-end justify-between gap-2">
            <Mascot mood="idle" size={110} message={`Hoi ${profile.name}!`} />
            {/* Daily challenge */}
            <motion.button
              data-testid="daily-challenge-card"
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => { speak(`Dagelijkse uitdaging: ${daily.text}`); }}
              className="flex-1 max-w-[220px] rounded-3xl bg-white/95 p-4 shadow-lg text-left"
            >
              <div className="flex items-center gap-2 text-tangerine-dark">
                <Target className="h-5 w-5 text-orange-500" />
                <span className="font-display text-sm font-bold text-slate-500">Dagelijkse uitdaging</span>
              </div>
              <p className="mt-1 font-display text-lg font-bold text-slate-800">{daily.text}</p>
              <p className="text-xs font-bold text-yellow-600">🪙 +{daily.reward.coins} munten</p>
            </motion.button>
          </div>

          <h2 className="mb-4 mt-2 font-display text-3xl font-bold text-slate-800 drop-shadow-sm">Kies je eiland</h2>

          {/* Islands */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {visibleSubjects.map((s, i) => (
              <motion.button
                key={s.id}
                data-testid={`island-${s.id}`}
                initial={{ scale: 0, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ delay: i * 0.06, type: "spring", stiffness: 200 }}
                whileHover={{ scale: s.unlocked ? 1.05 : 1, y: -4 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => openSubject(s)}
                className="relative overflow-hidden rounded-[1.75rem] p-4 shadow-xl text-white"
                style={{ background: `linear-gradient(160deg, ${s.color}, ${s.colorDark})` }}
              >
                {!s.unlocked && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px]">
                    <Lock className="h-8 w-8 text-white/90" />
                  </div>
                )}
                <motion.div
                  animate={{ y: [0, -6, 0] }} transition={{ duration: 2.5 + i * 0.2, repeat: Infinity }}
                  className="text-5xl drop-shadow"
                >
                  {s.emoji}
                </motion.div>
                <p className="mt-2 font-display text-xl font-bold drop-shadow">{s.title}</p>
                {s.unlocked && (
                  <p className="text-xs font-bold text-white/80">{s.lessons.length} lessen</p>
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Bottom nav */}
        <div className="fixed bottom-0 left-0 right-0 z-40 mx-auto flex max-w-3xl gap-2 p-3">
          <NavBtn testid="nav-pet" icon={PawPrint} label="Huisdier" color="#F472B6" onClick={() => navigate("/huisdier")} />
          <NavBtn testid="nav-rewards" icon={Trophy} label="Beloningen" color="#EAB308" onClick={() => navigate("/beloningen")} />
          <NavBtn testid="nav-settings" icon={Settings} label="Instellingen" color="#38BDF8" onClick={() => navigate("/instellingen")} />
          <NavBtn testid="nav-parents" icon={ShieldCheck} label="Ouders" color="#4ADE80" onClick={() => navigate("/ouders")} />
        </div>
      </div>
    </div>
  );
}
